import React, { useEffect, useState } from 'react';
import { authFetch } from '../authFetch';
import toast from 'react-hot-toast';
import { settingsAPI, itemsAPI, authAPI } from '../api';

const SETTING_TYPES = [
  { key: 'department', label: 'Departments' },
  { key: 'product_status', label: 'Product Statuses' },
  { key: 'project_status', label: 'Project Statuses' },
  { key: 'product_type', label: 'Product Types' },
  { key: 'category', label: 'Product Categories' },
  { key: 'sub_category', label: 'Sub Categories' },
];

export default function Settings() {
  const [templates, setTemplates] = React.useState([]);
  const [showEditType, setShowEditType] = React.useState(false);
  const [editType, setEditType] = React.useState(null);
  const [typeForm, setTypeForm] = React.useState({ value: '', description: '', dev_cost: '' });
  const [showTemplateForm, setShowTemplateForm] = React.useState(false);
  const [templateForm, setTemplateForm] = React.useState({ name: '', type: 'packing', description: '', items: [] });
  const [editTemplate, setEditTemplate] = React.useState(null);
  const [templateFilter, setTemplateFilter] = React.useState('all');

  const loadTemplates = () => {
    const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
    authFetch(base + '/costing-templates').then(r=>r.json()).then(setTemplates).catch(()=>{});
  };

  React.useEffect(() => { loadTemplates(); }, []);

  const saveTemplate = async () => {
    const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
    const url = editTemplate ? base + '/costing-templates/' + editTemplate.id : base + '/costing-templates';
    const method = editTemplate ? 'PUT' : 'POST';
    await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(templateForm) });
    setShowTemplateForm(false);
    setEditTemplate(null);
    setTemplateForm({ name: '', type: 'packing', description: '', items: [] });
    loadTemplates();
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
    await authFetch(base + '/costing-templates/' + id, { method: 'DELETE' });
    loadTemplates();
  };

  const addTemplateItem = () => {
    if (templateForm.type === 'packing') {
      setTemplateForm(f => ({...f, items: [...f.items, { item_code: '', item_name: '', quantity: 0, unit: 'kg' }]}));
    } else {
      setTemplateForm(f => ({...f, items: [...f.items, { operation_name: '', rate: 0, unit: 'job' }]}));
    }
  };

  const updateTemplateItem = (idx, field, val) => {
    setTemplateForm(f => ({ ...f, items: f.items.map((it,i) => i===idx ? {...it, [field]: val} : it) }));
  };

  const removeTemplateItem = (idx) => {
    setTemplateForm(f => ({ ...f, items: f.items.filter((_,i) => i !== idx) }));
  };
  const [regenerating, setRegenerating] = React.useState(false);
  const [regenResult, setRegenResult] = React.useState(null);

  const handleRegenerate = async () => {
    if (!window.confirm('This will regenerate ALL project and product codes. Continue?')) return;
    setRegenerating(true);
    try {
      const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
      const r = await authFetch(base + '/regenerate-codes', { method: 'POST' });
      const data = await r.json();
      setRegenResult(data);
    } catch(e) { alert('Failed: ' + e.message); }
    setRegenerating(false);
  };
  const [settings, setSettings] = useState({});
  const [inputs, setInputs] = useState({});
  const [items, setItems] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [itemForm, setItemForm] = useState({ item_code: '', item_name: '', unit: '', unit_cost: '', category: '' });
  const [editItem, setEditItem] = useState(null);
  const [activeTab, setActiveTab] = useState('departments');
  const [itemSearch, setItemSearch] = useState('');
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('npd_user')); } catch { return null; } })();
  const isAdmin = currentUser?.username === 'admin';
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editUserName, setEditUserName] = useState('');
  const [userForm, setUserForm] = useState({ username: '', password: '', name: '' });

  const loadUsers = () => authAPI.getUsers().then(r => setUsers(r.data));

  const loadSettings = () => settingsAPI.getAll().then(r => setSettings(r.data));
  const loadItems = () => itemsAPI.getAll().then(r => setItems(r.data));

  useEffect(() => { loadSettings(); loadItems(); loadUsers(); }, []);

  const updateSetting = async (id, data) => {
    const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
    await authFetch(base + '/settings/' + id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    toast.success('Updated');
    loadSettings();
  };

  const saveType = async () => {
    if (!typeForm.value) return toast.error('Name required');
    if (editType) {
      await updateSetting(editType.id, typeForm);
    } else {
      const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
      await authFetch(base + '/settings', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'product_type', ...typeForm }) });
      toast.success('Added');
    }
    setShowEditType(false);
    setEditType(null);
    setTypeForm({ value: '', description: '', dev_cost: '' });
    loadSettings();
  };

  const addSetting = async (type) => {
    const val = inputs[type]?.trim();
    if (!val) return;
    try {
      await settingsAPI.create(type, val);
      toast.success('Added');
      setInputs({ ...inputs, [type]: '' });
      loadSettings();
    } catch { toast.error('Already exists'); }
  };

  const deleteSetting = async (id) => {
    await settingsAPI.delete(id);
    toast.success('Deleted');
    loadSettings();
  };

  const saveItem = async () => {
    if (!itemForm.item_code || !itemForm.item_name) return toast.error('Code and name required');
    try {
      if (editItem) {
        await itemsAPI.update(editItem.id, itemForm);
        toast.success('Item updated');
      } else {
        await itemsAPI.create(itemForm);
        toast.success('Item added');
      }
      setShowAddItem(false);
      setEditItem(null);
      setItemForm({ item_code: '', item_name: '', unit: '', unit_cost: '', category: '' });
      loadItems();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const deleteItem = async (id) => {
    await itemsAPI.delete(id);
    toast.success('Item removed');
    loadItems();
  };

  const uploadItems = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const isExcel = file.name.match(/\.(xlsx|xls)$/i);
      const r = isExcel ? await itemsAPI.uploadExcel(file) : await itemsAPI.uploadCSV(file);
      toast.success(`Uploaded ${r.data.count} items`);
      loadItems();
    } catch (err) { toast.error(err.response?.data?.error || 'Upload failed'); }
    e.target.value = '';
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-subtitle">Manage departments, statuses, product types and item master</div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={handleRegenerate} disabled={regenerating}>
          {regenerating ? 'Regenerating...' : '🔄 Regenerate All Codes'}
        </button>
      </div>
      {regenResult && (
        <div style={{ background: 'var(--green-light)', color: 'var(--green)', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          ✅ Codes regenerated — {regenResult.projects} projects, {regenResult.products} products updated!
          <button onClick={() => setRegenResult(null)} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green)' }}>✕</button>
        </div>
      )}

      <div className="tabs">
        {SETTING_TYPES.map(t => (
          <button key={t.key} className={`tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
        <button className={`tab ${activeTab === 'templates' ? 'active' : ''}`} onClick={() => setActiveTab('templates')}>Cost Templates</button>
        <button className={`tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>Users ({users.length})</button>
      </div>

      {SETTING_TYPES.filter(t => t.key !== 'product_type').map(t => activeTab === t.key && (
        <div key={t.key} className="card" style={{ maxWidth: 500 }}>
          <div className="card-title">{t.label}</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              placeholder={`Add new ${t.label.toLowerCase().slice(0, -1)}...`}
              value={inputs[t.key] || ''}
              onChange={e => setInputs({ ...inputs, [t.key]: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && addSetting(t.key)}
              style={{ flex: 1 }}
            />
            <button className="btn btn-primary" onClick={() => addSetting(t.key)}>Add</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(settings[t.key] || []).map(s => (
              <div key={s.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: 6,
                border: '1px solid var(--border)'
              }}>
                <span className={t.key === 'product_status' ? `badge badge-${s.value}` : ''} style={t.key !== 'product_status' ? { fontSize: 13 } : {}}>
                  {s.value}
                </span>
                <button className="btn btn-danger btn-sm" onClick={() => deleteSetting(s.id)}>Delete</button>
              </div>
            ))}
            {!(settings[t.key] || []).length && <div className="text-muted" style={{ fontSize: 13 }}>No {t.label.toLowerCase()} yet</div>}
          </div>
        </div>
      ))}

      {/* Product Types - Enhanced with description and dev cost */}
      {activeTab === 'product_type' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div className="card-title" style={{ margin: 0 }}>Product Types / Categories</div>
            <button className="btn btn-primary btn-sm" onClick={() => { setEditType(null); setTypeForm({ value: '', description: '', dev_cost: '' }); setShowEditType(true); }}>+ Add Type</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Description</th>
                  <th className="text-right">Dev Cost (AED)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(settings['product_type'] || []).map(s => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600, color: 'var(--accent)' }}>{s.value}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.description || '—'}</td>
                    <td className="text-right cost-value">{s.dev_cost > 0 ? 'AED ' + parseFloat(s.dev_cost).toLocaleString('en-AE', { maximumFractionDigits: 2 }) : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setEditType(s); setTypeForm({ value: s.value, description: s.description || '', dev_cost: s.dev_cost || '' }); setShowEditType(true); }}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => deleteSetting(s.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!(settings['product_type'] || []).length && <tr><td colSpan={4} className="text-muted" style={{ textAlign: 'center', padding: 20 }}>No product types yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit/Add Product Type Modal */}
      {showEditType && (
        <div className="modal-overlay" onClick={() => setShowEditType(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <div className="modal-title">{editType ? 'Edit' : 'Add'} Product Type</div>
              <button className="modal-close" onClick={() => setShowEditType(false)}>×</button>
            </div>
            <div style={{ padding: '8px 0' }}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Category Name *</label>
                <input value={typeForm.value} onChange={e => setTypeForm({...typeForm, value: e.target.value})} placeholder="e.g. Category 1" style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #eee', borderRadius: 8 }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Description</label>
                <textarea value={typeForm.description} onChange={e => setTypeForm({...typeForm, description: e.target.value})} placeholder="What products belong to this category..." rows={2} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #eee', borderRadius: 8, resize: 'vertical' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>R&D Development Cost (AED)</label>
                <input type="number" step="0.01" value={typeForm.dev_cost} onChange={e => setTypeForm({...typeForm, dev_cost: e.target.value})} placeholder="e.g. 500.00" style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #eee', borderRadius: 8 }} />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>This fixed R&D cost will be automatically added to every product of this type</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={saveType}>{editType ? 'Save' : 'Add'}</button>
              <button className="btn btn-secondary" onClick={() => setShowEditType(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'items' && (
        <div className="card">
          <div className="flex-between mb-4">
            <div className="card-title" style={{ margin: 0 }}>Item Master — {items.length} items</div>
            <div className="flex gap-2">
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                ↑ Upload Excel / CSV
                <input type="file" accept=".xlsx,.xls,.csv" onChange={uploadItems} style={{ display: 'none' }} />
              </label>
              <button className="btn btn-primary btn-sm" onClick={() => { setEditItem(null); setItemForm({ item_code: '', item_name: '', unit: '', unit_cost: '', category: '' }); setShowAddItem(true); }}>
                + Add Item
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <input
              placeholder="🔍 Search by item code or name..."
              value={itemSearch}
              onChange={e => setItemSearch(e.target.value)}
              style={{ flex: 1, fontSize: 13 }}
            />
            {itemSearch && <button className="btn btn-ghost btn-sm" onClick={() => setItemSearch('')}>✕ Clear</button>}
          </div>
          <div className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>
            Excel/CSV columns: <span className="mono">Item Code, Item Name, Unit, Unit Cost, Category</span>
          </div>
          {items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">◈</div>
              <p>No items yet. Add manually or upload a CSV.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Name</th>
                    <th>Unit</th>
                    <th className="text-right">Unit Cost (AED )</th>
                    <th>Category</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.filter(item => { if (!itemSearch) return true; const words = itemSearch.toLowerCase().split(/\s+/).filter(Boolean); const hay = ((item.item_code||'') + ' ' + (item.item_name||'')).toLowerCase(); return words.every(w => hay.includes(w)); }).map(item => (
                    <tr key={item.id}>
                      <td className="mono">{item.item_code}</td>
                      <td>{item.item_name}</td>
                      <td>{item.unit}</td>
                      <td className="text-right cost-value">AED {(item.unit_cost || 0).toLocaleString('en-AE', { maximumFractionDigits: 2 })}</td>
                      <td className="text-muted">{item.category || '—'}</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn btn-ghost btn-sm" onClick={() => { setEditItem(item); setItemForm({ item_code: item.item_code, item_name: item.item_name, unit: item.unit || '', unit_cost: item.unit_cost || '', category: item.category || '' }); setShowAddItem(true); }}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => deleteItem(item.id)}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'templates' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {['all','packing','operation'].map(t => (
                <button key={t} className={`btn btn-sm ${templateFilter===t?'btn-primary':'btn-secondary'}`} onClick={() => setTemplateFilter(t)}>
                  {t === 'all' ? 'All' : t === 'packing' ? '📦 Packing' : '⚙️ Operation'}
                </button>
              ))}
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => { setShowTemplateForm(true); setEditTemplate(null); setTemplateForm({ name: '', type: 'packing', description: '', items: [] }); }}>
              + New Template
            </button>
          </div>

          {templates.filter(t => templateFilter === 'all' || t.type === templateFilter).length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">📋</div><p>No templates yet. Create your first template!</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {templates.filter(t => templateFilter === 'all' || t.type === templateFilter).map(t => (
                <div key={t.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      <span className={t.type === 'packing' ? 'badge badge-blue' : 'badge badge-amber'}>{t.type === 'packing' ? '📦 Packing' : '⚙️ Operation'}</span>
                      {t.description && <span style={{ marginLeft: 8 }}>{t.description}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={async () => {
                      const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
                      const r = await authFetch(base + '/costing-templates/' + t.id);
                      const data = await r.json();
                      setTemplateForm({ name: data.name, type: data.type, description: data.description||'', items: data.items||[] });
                      setEditTemplate(data);
                      setShowTemplateForm(true);
                    }}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteTemplate(t.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showTemplateForm && (
            <div className="modal-overlay">
              <div className="modal modal-lg">
                <div className="modal-header">
                  <div className="modal-title">{editTemplate ? 'Edit Template' : 'New Cost Template'}</div>
                  <button className="modal-close" onClick={() => setShowTemplateForm(false)}>✕</button>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Template Name *</label>
                    <input value={templateForm.name} onChange={e => setTemplateForm(f=>({...f, name: e.target.value}))} placeholder="e.g. Standard Carton Pack" />
                  </div>
                  <div className="form-group">
                    <label>Type *</label>
                    <select value={templateForm.type} onChange={e => setTemplateForm(f=>({...f, type: e.target.value, items: []}))} disabled={!!editTemplate}>
                      <option value="packing">📦 Packing Materials</option>
                      <option value="operation">⚙️ Operation Costs</option>
                    </select>
                  </div>
                  <div className="form-group full">
                    <label>Description</label>
                    <input value={templateForm.description} onChange={e => setTemplateForm(f=>({...f, description: e.target.value}))} placeholder="Optional description" />
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{templateForm.type === 'packing' ? 'Packing Materials' : 'Operations'}</div>
                    <button className="btn btn-secondary btn-sm" onClick={addTemplateItem}>+ Add Item</button>
                  </div>
                  {templateForm.items.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px', background: 'var(--bg-elevated)', borderRadius: 8, textAlign: 'center' }}>No items yet. Click + Add Item.</div>
                  ) : (
                    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                      {templateForm.type === 'packing' ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead><tr style={{ background: 'var(--bg-elevated)' }}>
                            <th style={{ padding: '7px 10px', fontSize: 11, textAlign: 'left' }}>Item Code</th>
                            <th style={{ padding: '7px 10px', fontSize: 11, textAlign: 'left' }}>Item Name</th>
                            <th style={{ padding: '7px 10px', fontSize: 11, textAlign: 'center' }}>Qty</th>
                            <th style={{ padding: '7px 10px', fontSize: 11, textAlign: 'center' }}>Unit</th>
                            <th style={{ padding: '7px 10px', fontSize: 11 }}></th>
                          </tr></thead>
                          <tbody>
                            {templateForm.items.map((item, idx) => (
                              <tr key={idx} style={{ borderTop: '1px solid var(--border)' }}>
                                <td style={{ padding: '6px 8px' }}><input value={item.item_code} onChange={e => updateTemplateItem(idx,'item_code',e.target.value)} style={{ width: '100%', padding: '4px 6px', fontSize: 12, fontFamily: 'monospace' }} placeholder="Item code" /></td>
                                <td style={{ padding: '6px 8px' }}><input value={item.item_name} onChange={e => updateTemplateItem(idx,'item_name',e.target.value)} style={{ width: '100%', padding: '4px 6px', fontSize: 12 }} placeholder="Item name" /></td>
                                <td style={{ padding: '6px 8px' }}><input type="number" value={item.quantity} onChange={e => updateTemplateItem(idx,'quantity',e.target.value)} style={{ width: '70px', padding: '4px 6px', fontSize: 12, textAlign: 'right' }} /></td>
                                <td style={{ padding: '6px 8px' }}><input value={item.unit} onChange={e => updateTemplateItem(idx,'unit',e.target.value)} style={{ width: '60px', padding: '4px 6px', fontSize: 12 }} /></td>
                                <td style={{ padding: '6px 8px', textAlign: 'center' }}><button onClick={() => removeTemplateItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 16 }}>✕</button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead><tr style={{ background: 'var(--bg-elevated)' }}>
                            <th style={{ padding: '7px 10px', fontSize: 11, textAlign: 'left' }}>Operation Name</th>
                            <th style={{ padding: '7px 10px', fontSize: 11, textAlign: 'right' }}>Rate (AED)</th>
                            <th style={{ padding: '7px 10px', fontSize: 11, textAlign: 'center' }}>Unit</th>
                            <th style={{ padding: '7px 10px', fontSize: 11 }}></th>
                          </tr></thead>
                          <tbody>
                            {templateForm.items.map((item, idx) => (
                              <tr key={idx} style={{ borderTop: '1px solid var(--border)' }}>
                                <td style={{ padding: '6px 8px' }}><input value={item.operation_name} onChange={e => updateTemplateItem(idx,'operation_name',e.target.value)} style={{ width: '100%', padding: '4px 6px', fontSize: 12 }} placeholder="e.g. Cleaning & Packing" /></td>
                                <td style={{ padding: '6px 8px' }}><input type="number" value={item.rate} onChange={e => updateTemplateItem(idx,'rate',e.target.value)} style={{ width: '100px', padding: '4px 6px', fontSize: 12, textAlign: 'right' }} /></td>
                                <td style={{ padding: '6px 8px' }}><input value={item.unit} onChange={e => updateTemplateItem(idx,'unit',e.target.value)} style={{ width: '70px', padding: '4px 6px', fontSize: 12 }} placeholder="job/kg/unit" /></td>
                                <td style={{ padding: '6px 8px', textAlign: 'center' }}><button onClick={() => removeTemplateItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 16 }}>✕</button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowTemplateForm(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={saveTemplate} disabled={!templateForm.name}>
                    {editTemplate ? 'Update Template' : 'Save Template'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="card">
          <div className="flex-between mb-4">
            <div className="card-title" style={{ margin: 0 }}>User Management</div>
            <button className="btn btn-primary btn-sm" onClick={() => { setUserForm({ username: '', password: '', name: '' }); setShowAddUser(true); }}>+ Add User</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Username</th><th>Role</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 500 }}>{u.name}</td>
                    <td className="mono">{u.username}</td>
                    <td>
                      {isAdmin ? (
                        <select value={u.role||'standard'} onChange={async e => { await authAPI.updateUser(u.id, { password: u.password||'', name: u.name, active: u.active, role: e.target.value }); loadUsers(); toast.success('Role updated'); }} style={{ width: 'auto', padding: '3px 8px', fontSize: 12 }}>
                          <option value="standard">Standard</option>
                          <option value="approver">Approver</option>
                          <option value="field">Field (Photo Only)</option>
                          <option value="qc">QC (Specs)</option>
                          <option value="qc_manager">QC Manager (Specs)</option>
                          <option value="op_manager">OP Manager (Specs)</option>
                          <option value="spec_viewer">Spec Viewer</option>
                        </select>
                      ) : (
                        <span className="mono" style={{ fontSize: 11 }}>{u.role||'standard'}</span>
                      )}
                    </td>
                    <td><span className={`badge ${u.active ? 'badge-active' : 'badge-trial'}`}>{u.active ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div className="flex gap-2">
                        {isAdmin && <button className="btn btn-ghost btn-sm" onClick={() => { setEditUser(u); setEditUserName(u.name); }}>✏ Edit</button>}
                        {isAdmin && <button className="btn btn-secondary btn-sm" onClick={() => { setResetUser(u); setNewPassword(''); }}>Reset Pwd</button>}
                        {isAdmin && u.active && <button className="btn btn-danger btn-sm" onClick={async () => { await authAPI.updateUser(u.id, { password: u.password||'', name: u.name, active: 0, role: u.role||'standard' }); loadUsers(); toast.success('User deactivated'); }}>Deactivate</button>}
                        {isAdmin && !u.active && <button className="btn btn-secondary btn-sm" onClick={async () => { await authAPI.updateUser(u.id, { password: u.password||'', name: u.name, active: 1, role: u.role||'standard' }); loadUsers(); toast.success('User reactivated'); }}>Reactivate</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAddUser && (
        <div className="modal-overlay" onClick={() => setShowAddUser(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Add User</div>
              <button className="modal-close" onClick={() => setShowAddUser(false)}>×</button>
            </div>
            <div className="form-grid">
              <div className="form-group full"><label>Full Name</label><input placeholder="e.g. John Smith" value={userForm.name} onChange={e => setUserForm({ ...userForm, name: e.target.value })} /></div>
              <div className="form-group"><label>Username *</label><input placeholder="e.g. john" value={userForm.username} onChange={e => setUserForm({ ...userForm, username: e.target.value })} /></div>
              <div className="form-group"><label>Password *</label><input type="password" placeholder="Set a password" value={userForm.password} onChange={e => setUserForm({ ...userForm, password: e.target.value })} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddUser(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={async () => {
                if (!userForm.username || !userForm.password) return toast.error('Username and password required');
                try { await authAPI.createUser(userForm); toast.success('User added'); setShowAddUser(false); loadUsers(); }
                catch(e) { toast.error(e.response?.data?.error || 'Error'); }
              }}>Add User</button>
            </div>
          </div>
        </div>
      )}

      {editUser && (
        <div className="modal-overlay" onClick={() => setEditUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Edit User</div>
              <button className="modal-close" onClick={() => setEditUser(null)}>×</button>
            </div>
            <div className="form-group">
              <label>Full Name *</label>
              <input placeholder="Full name" value={editUserName} onChange={e => setEditUserName(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginTop: 10 }}>
              <label>Username</label>
              <input value={editUser.username} disabled style={{ opacity: 0.5 }} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditUser(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={async () => {
                if (!editUserName) return toast.error('Name required');
                await authAPI.updateUser(editUser.id, { password: editUser.password||'', name: editUserName, active: editUser.active, role: editUser.role||'standard' });
                toast.success('User updated');
                setEditUser(null);
                loadUsers();
              }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {resetUser && (
        <div className="modal-overlay" onClick={() => setResetUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Reset Password — {resetUser.name}</div>
              <button className="modal-close" onClick={() => setResetUser(null)}>×</button>
            </div>
            <div className="form-group">
              <label>New Password *</label>
              <input type="password" placeholder="Enter new password" value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="new-password" />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setResetUser(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={async () => {
                if (!newPassword) return toast.error('Enter a password');
                await authAPI.updateUser(resetUser.id, { password: newPassword, name: resetUser.name, active: resetUser.active });
                toast.success('Password reset successfully');
                setResetUser(null);
              }}>Reset Password</button>
            </div>
          </div>
        </div>
      )}

      {showAddItem && (
        <div className="modal-overlay" onClick={() => setShowAddItem(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{editItem ? 'Edit Item' : 'Add Item'}</div>
              <button className="modal-close" onClick={() => setShowAddItem(false)}>×</button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Item Code *</label>
                <input placeholder="e.g. RM-001" value={itemForm.item_code} onChange={e => setItemForm({ ...itemForm, item_code: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Item Name *</label>
                <input placeholder="e.g. Wheat Flour" value={itemForm.item_name} onChange={e => setItemForm({ ...itemForm, item_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Unit</label>
                <input placeholder="e.g. kg, L, pcs" value={itemForm.unit} onChange={e => setItemForm({ ...itemForm, unit: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Unit Cost (AED )</label>
                <input type="number" placeholder="0.00" value={itemForm.unit_cost} onChange={e => setItemForm({ ...itemForm, unit_cost: e.target.value })} />
              </div>
              <div className="form-group full">
                <label>Category</label>
                <input placeholder="e.g. Raw Material, Packaging" value={itemForm.category} onChange={e => setItemForm({ ...itemForm, category: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddItem(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveItem}>{editItem ? 'Update' : 'Add Item'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
